import type { ReactNode } from "react"
import ModuleLayout, { type BreadcrumbItem, type ModuleMetric } from "../admin/ModuleLayout"
import { EntityDetailNavLayout, type EntityDetailNavGroup } from "../admin/EntityDetailNav"

export type SeedbedTabKey = "info" | "groups" | "courses"

const SEEDBED_TAB_LABELS: Record<SeedbedTabKey, string> = {
  info: "Información",
  groups: "Grupos",
  courses: "Cursos",
}

interface SeedbedShellProps {
  mode: "create" | "edit"
  activeTab: SeedbedTabKey
  onTabChange: (tab: SeedbedTabKey) => void
  breadcrumbs: BreadcrumbItem[]
  title: string
  subtitle: string
  metrics?: ModuleMetric[]
  headerActions?: ReactNode
  children: ReactNode
}

/**
 * Shell compartido para crear/ver/editar un semillero (mismo patrón que
 * TeamShell/EventShell): al crear se muestra la misma barra de tabs que al
 * ver/editar, con solo "Información" habilitada y el resto visible-pero-
 * bloqueado hasta guardar.
 */
export function SeedbedShell({
  mode,
  activeTab,
  onTabChange,
  breadcrumbs,
  title,
  subtitle,
  metrics,
  headerActions,
  children,
}: SeedbedShellProps) {
  const locked = (key: SeedbedTabKey) => mode === "create" && key !== "info"
  const groups: EntityDetailNavGroup<SeedbedTabKey>[] = [
    { label: "GENERAL", items: [
      { key: "info", label: SEEDBED_TAB_LABELS.info, locked: locked("info") },
    ] },
    { label: "ACADÉMICO", items: [
      { key: "courses", label: SEEDBED_TAB_LABELS.courses, locked: locked("courses") },
      { key: "groups", label: SEEDBED_TAB_LABELS.groups, locked: locked("groups") },
    ] },
  ]

  return (
    <ModuleLayout
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
      tabs={[{ label: "Semilleros", to: "/programs/seedbeds", active: true }]}
      metrics={metrics ?? []}
      actions={headerActions}
    >
      <EntityDetailNavLayout groups={groups} activeKey={activeTab} onChange={(key) => !locked(key) && onTabChange(key)}>
        {children}
      </EntityDetailNavLayout>
    </ModuleLayout>
  )
}
